
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { LoadingState, ErrorState } from "@/components/features/states";
import { formatRelativeTime } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Send, Star, Phone, Info, Languages, Loader2 } from "lucide-react";
import i18n from "@/lib/i18n";

export function ChatView({ jobId }: { jobId: string }) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [translatingIds, setTranslatingIds] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["chat", jobId],
    queryFn: () => api.chat.messages.list(jobId),
    refetchInterval: 3000,
  });
  const { data: jobData } = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => api.jobs.get(jobId),
  });

  const messages = data?.data?.messages ?? [];
  const job = jobData?.data?.job;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Auto-translate incoming (not mine) messages when enabled.
  useEffect(() => {
    if (!autoTranslate) return;
    const targetLang = i18n.language || "en";
    const toTranslate = messages.filter(
      (m) =>
        m.type === "text" &&
        m.senderId !== user?.id &&
        (!m.translatedContent || m.translatedLang !== targetLang)
    );
    if (toTranslate.length === 0) return;
    // Translate up to 3 at a time to avoid flooding.
    let cancelled = false;
    (async () => {
      for (const m of toTranslate.slice(0, 3)) {
        if (cancelled) break;
        setTranslatingIds((s) => new Set(s).add(m.id));
        const res = await api.translate.translate({ messageId: m.id, targetLang });
        setTranslatingIds((s) => {
          const next = new Set(s);
          next.delete(m.id);
          return next;
        });
        if (res.ok) {
          qc.invalidateQueries({ queryKey: ["chat", jobId] });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [autoTranslate, messages.length]);

  if (isLoading) return <LoadingState label={t("common.loading")} />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("errors.generic")} onRetry={refetch} />;

  const otherParty = job
    ? job.customerId === user?.id
      ? job.provider
      : job.customer
    : null;
  const otherName = otherParty?.name || t("chat.title");

  const send = async () => {
    if (!text.trim() || !job) return;
    const receiverId = job.customerId === user?.id ? job.providerId : job.customerId;
    if (!receiverId) return toast.error(t("chat.noMessagesHint"));
    setSending(true);
    setText("");
    const res = await api.chat.messages.send({ jobId, receiverId, content: text.trim() });
    setSending(false);
    if (!res.ok) {
      setText(text);
      return toast.error(res.error || t("errors.generic"));
    }
    qc.invalidateQueries({ queryKey: ["chat", jobId] });
  };

  const translateOne = async (messageId: string) => {
    const targetLang = i18n.language || "en";
    setTranslatingIds((s) => new Set(s).add(messageId));
    const res = await api.translate.translate({ messageId, targetLang });
    setTranslatingIds((s) => {
      const next = new Set(s);
      next.delete(messageId);
      return next;
    });
    if (!res.ok) return toast.error(t("chat.translationFailed"));
    qc.invalidateQueries({ queryKey: ["chat", jobId] });
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border/60 pb-3">
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate(`app/jobs/${jobId}`)}>
          <ArrowLeft className="h-4 w-4 rtl-flip" />
        </Button>
        <Avatar className="h-9 w-9">
          <AvatarFallback className="bg-primary/10 text-xs font-bold text-primary">
            {otherName.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <p className="font-semibold">{otherName}</p>
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            {otherParty && (
              <>
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {otherParty.rating?.toFixed(1)}
              </>
            )}
            {job && <span className="ml-1">· {job.code}</span>}
          </p>
        </div>
        {/* Phase 3: auto-translate toggle */}
        <label className="flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1" title={t("chat.autoTranslate")}>
          <Languages className="h-3.5 w-3.5 text-primary" />
          <Switch
            checked={autoTranslate}
            onCheckedChange={setAutoTranslate}
            aria-label={t("chat.autoTranslate")}
          />
        </label>
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate(`app/jobs/${jobId}`)}>
          <Info className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="scroll-area flex-1 space-y-2 overflow-y-auto py-4">
        {messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">
            <div>
              <Send className="mx-auto mb-2 h-8 w-8 opacity-40" />
              <p>{t("chat.noMessages")}</p>
              <p className="text-xs">{t("chat.noMessagesHint")}</p>
            </div>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.senderId === user?.id;
            const showTranslation = m.translatedContent && m.translatedContent !== m.content;
            const isTranslating = translatingIds.has(m.id);
            return (
              <div key={m.id} className={`flex ${mine ? "chat-mine justify-end" : "chat-theirs justify-start"}`}>
                <div className="max-w-[78%]">
                  <div
                    className={`rounded-2xl px-3.5 py-2 text-sm ${
                      mine
                        ? "rounded-br-sm bg-primary text-primary-foreground"
                        : "rounded-bl-sm bg-muted text-foreground"
                    }`}
                  >
                    {m.type === "system" ? (
                      <span className="italic opacity-70">{m.content}</span>
                    ) : showTranslation ? (
                      <div className="space-y-1">
                        <p className="italic opacity-70 line-through decoration-1">{m.content}</p>
                        <p>{m.translatedContent}</p>
                        <p className="flex items-center gap-1 text-[9px] opacity-60">
                          <Languages className="h-2.5 w-2.5" /> {t("chat.translated")}
                        </p>
                      </div>
                    ) : (
                      m.content
                    )}
                  </div>
                  <div className={`mt-0.5 flex items-center gap-1.5 text-[10px] text-muted-foreground ${mine ? "justify-end" : "justify-start"}`}>
                    <span>{formatRelativeTime(m.createdAt)}</span>
                    {m.type === "text" && !showTranslation && (
                      <button
                        onClick={() => translateOne(m.id)}
                        disabled={isTranslating}
                        className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 hover:bg-muted disabled:opacity-50"
                        aria-label={t("chat.translate")}
                      >
                        {isTranslating ? (
                          <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        ) : (
                          <Languages className="h-2.5 w-2.5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-border/60 pt-3">
        <div className="flex gap-2">
          <Input
            placeholder={t("chat.typeMessage")}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={sending}
          />
          <Button onClick={send} disabled={sending || !text.trim()}>
            <Send className="h-4 w-4 rtl-flip" />
          </Button>
        </div>
      </div>
    </div>
  );
}
